import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ShippingResponseRowType,
  RequestShippingParams,
  ShippingRowType,
  ShippingCaseShifting,
} from "./types";
import {
  exportShippingApi,
  getShippingCasesListApi,
  getShippingCasesShiftingApi,
} from "./api";
import { AppThunk } from "../../store";

type ShippingListType = {
  shippingListState: {
    items: ShippingResponseRowType[] | null;
    total: number;
    loading: boolean;
  };
  shippingCaseShifting: {
    items: ShippingCaseShifting[] | null;
    size: number;
    page: number;
    pages: number;
    total: number;
    loading: boolean;
  };
  error: string | null;
  success: string | null;
  filters: Partial<ShippingRowType> | null;
  export: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: ShippingListType = {
  shippingListState: {
    items: null,
    total: 0,
    loading: false,
  },
  shippingCaseShifting: {
    items: null,
    total: 0,
    size: 0,
    page: 0,
    pages: 0,
    loading: false,
  },
  filters: null,
  error: "",
  success: "",
  export: {
    loading: false,
    error: null,
  },
};

const shippingList = createSlice({
  name: "shippingList",
  initialState: initialState,
  reducers: {
    getShippingStart(state: ShippingListType) {
      state.shippingListState.loading = true;
      state.error = null;
    },
    getShippingCaseShiftingStart(state: ShippingListType) {
      state.shippingCaseShifting.loading = true;
      state.error = null;
    },
    getShippingSuccess(
      state: ShippingListType,
      action: PayloadAction<{
        items: ShippingResponseRowType[];
        total: number;
      }>,
    ) {
      state.shippingListState.items = action.payload.items;
      state.shippingListState.total = action.payload.total;
      state.shippingListState.loading = false;
      state.error = null;
    },
    getShippingCaseShiftingSuccess(
      state: ShippingListType,
      action: PayloadAction<{
        items: ShippingCaseShifting[];
        total: number;
      }>,
    ) {
      state.shippingCaseShifting.items = action.payload.items;
      state.shippingCaseShifting.total = action.payload.total;
      state.shippingCaseShifting.loading = false;
      state.error = null;
    },
    getShippingFailure(state: ShippingListType, action: PayloadAction<string>) {
      state.shippingListState.loading = false;
      state.error = action.payload;
      state.shippingListState.items = null;
      state.shippingListState.total = 0;
    },
    setShippingFilters(
      state: ShippingListType,
      action: PayloadAction<Partial<ShippingRowType> | null>,
    ) {
      state.filters = action.payload;
    },
    getExportFailure(state: ShippingListType, action: PayloadAction<string>) {
      state.export.error = action.payload;
      state.export.loading = false;
    },
    hideWarning(state: ShippingListType) {
      state.error = null;
    },
    startExport(state: ShippingListType) {
      state.export = {
        loading: true,
        error: null,
      };
    },
    successExport(state: ShippingListType) {
      state.export.loading = false;
      state.export.error = null;
    },
    cleanData(
      state: ShippingListType,
      action: PayloadAction<keyof ShippingListType>,
    ) {
      state[action.payload] = {
        items: null,
        total: 0,
        loading: false,
      } as any;
    },
  },
});

export const {
  getShippingStart,
  getShippingSuccess,
  getShippingFailure,
  setShippingFilters,
  getExportFailure,
  hideWarning,
  startExport,
  successExport,
  getShippingCaseShiftingSuccess,
  getShippingCaseShiftingStart,
  cleanData,
} = shippingList.actions;

export default shippingList.reducer;

export const fetchShippingList =
  (params: RequestShippingParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getShippingStart());
      const { items, total } = await getShippingCasesListApi(params);
      dispatch(getShippingSuccess({ items, total }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getShippingFailure(error));
    }
  };

export const fetchShippingCasesShifting =
  ({ id, params }: { id: number; params: any }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getShippingCaseShiftingStart());
      const { items, total } = await getShippingCasesShiftingApi(id, params);
      dispatch(getShippingCaseShiftingSuccess({ items: items || [], total }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getShippingFailure(error));
    }
  };

export const exportShippingCases =
  (params: Partial<ShippingRowType>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportShippingApi(params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(getExportFailure("Downloading failed. Try again later."));
    }
  };
